import type { Element } from 'slate';
import { Editor } from 'slate';

import {
  isCheckboxElement,
  isInvisibleElement,
  isPageBreakElement,
  isTabElement,
  Queries,
} from '../internal';

export function withUnselectable<T extends Editor>(editor: T): T {
  const { positions } = editor;

  /**
   * Cause the selection to skip over non-selectable nodes, such as page break elements.
   */
  editor.isSelectable = (element: Element): boolean => {
    return (
      !isInvisibleElement(element) &&
      !isPageBreakElement(element) &&
      !isTabElement(element) &&
      !isCheckboxElement(element)
    );
  };

  /**
   * Do not return positions inside or to the left of invisible nodes. This augments the behavior of
   * various editor methods, such as Transforms.move, Editor.before, Editor.after and
   * Transforms.delete.
   */
  editor.positions = function* (options = {}) {
    const { at = editor.selection, unit = 'offset' } = options;
    if (!at) return;

    let isFirst = true;

    for (const point of positions({
      ...options,
      /**
       * This instructs Editor.nodes to skip returning any invisible node, preventing
       * Editor.positions from considering these nodes. This is especially important for the 'word'
       * unit to ensure that words are not interrupted. For example, when encountering
       * 'hello' [marker] 'world', it will see this as two adjacent text nodes, 'hello' 'world',
       * which is treated the same as a single uninterrupted text node, 'helloworld'.
       */
      skip: isInvisibleElement,
    })) {
      /**
       * Skip any point immediately before an invisible node.
       *
       * We make an exception for when the first point returned from positions, since Editor.after
       * assumes that the first point is returned, and functions incorrectly if this is not the
       * case.
       */
      if (!isFirst && Queries.isPointBeforeInvisible(editor, point)) {
        /**
         * When the unit is something other than 'offset', we need to tell it which point to
         * return instead, preventing it from skipping points inadvertently. This should be the
         * first valid point after all invisible nodes.
         */
        if (unit !== 'offset') {
          const after = Editor.after(editor, point);
          if (after) yield after;
        }

        continue;
      }

      isFirst = false;
      yield point;
    }
  };

  return editor;
}
